import { useEffect, useRef, useState } from 'react'
import { InlineSpinner } from './components/InlineSpinner'
import { ImageLightbox } from './components/ImageLightbox'
import { isOperatorFileViewableInTab, normalizeFileExt } from './platform/buxus/operatorFileOpen'
import { resolveOperatorFileBlob } from './platform/buxus/operatorFileResolve'
import { useOperatorSession } from './platform/buxus/OperatorSessionContext'
import type { ScreenInteractionContext } from './platform/analytics/interactionAnalytics'
import { trackFileViewed } from './platform/analytics/interactionAnalytics'

type OperatorInlineImageProps = {
  operatorId: string
  fileId: string
  fileExt?: string
  alt: string
  className?: string
  /** When true, clicking opens a fullscreen lightbox. */
  zoomable?: boolean
  /** When true, render nothing if the image cannot be loaded (e.g. page header logo). */
  hideFallback?: boolean
  analytics?: ScreenInteractionContext
}

/**
 * Inline operator image from IndexedDB (post-download manifest cache), with catalog SAS fallback.
 */
export function OperatorInlineImage({
  operatorId,
  fileId,
  fileExt,
  alt,
  className,
  zoomable = false,
  hideFallback = false,
  analytics,
}: OperatorInlineImageProps) {
  const { catalog, persisted } = useOperatorSession()
  const [src, setSrc] = useState<string | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [zoomOpen, setZoomOpen] = useState(false)
  const blobUrlRef = useRef<string | null>(null)

  const operatorVersion = persisted.entries[operatorId]?.cachedVersion ?? null

  useEffect(() => {
    let cancelled = false

    const clearBlob = () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
    }

    const load = async () => {
      setStatus('loading')
      setSrc(null)
      clearBlob()

      const ext = normalizeFileExt(fileExt)
      if (fileExt?.trim() && !isOperatorFileViewableInTab(ext)) {
        if (!cancelled) setStatus('error')
        return
      }

      try {
        const blob = await resolveOperatorFileBlob(
          operatorId,
          operatorVersion,
          fileId,
          fileExt,
          catalog as Record<string, unknown>[] | null,
        )
        if (cancelled) return
        if (!blob) {
          setStatus('error')
          return
        }
        const url = URL.createObjectURL(blob)
        blobUrlRef.current = url
        setSrc(url)
        setStatus('ready')
      } catch {
        if (!cancelled) setStatus('error')
      }
    }

    void load()

    return () => {
      cancelled = true
      clearBlob()
    }
  }, [operatorId, fileId, fileExt, operatorVersion, catalog])

  if (status === 'loading') {
    if (hideFallback) return null
    return (
      <div
        className={['operator-inline-image-shell', 'operator-inline-image-shell--loading', className]
          .filter(Boolean)
          .join(' ')}
      >
        <InlineSpinner label={`Loading ${alt}`} />
      </div>
    )
  }

  if (status === 'error' || !src) {
    if (hideFallback) return null
    return (
      <p className="operator-inline-image-fallback">
        Image unavailable — re-download the operator on Download and try again.
      </p>
    )
  }

  return (
    <>
      {zoomable ? (
        <button
          type="button"
          className={['operator-inline-image-zoom-trigger', className].filter(Boolean).join(' ')}
          onClick={() => {
            if (analytics) {
              trackFileViewed({ ...analytics, fileId, title: alt })
            }
            setZoomOpen(true)
          }}
          aria-label={`View full size: ${alt}`}
        >
          <img
            src={src}
            alt={alt}
            className="operator-inline-image"
            loading="lazy"
            decoding="async"
          />
        </button>
      ) : (
        <img
          src={src}
          alt={alt}
          className={['operator-inline-image', className].filter(Boolean).join(' ')}
          loading="lazy"
          decoding="async"
        />
      )}
      {zoomOpen && src ? <ImageLightbox src={src} alt={alt} onClose={() => setZoomOpen(false)} /> : null}
    </>
  )
}
